import { createAsyncThunk } from '@reduxjs/toolkit';
import { SelectOption } from 'src/types/SelectOption';
import { SeniorSnapshotFileListItemModel } from 'src/types/SeniorSnapshotFileListItemModel';
import { SeniorSnapshotFormViewModel } from 'src/types/SeniorSnapshotFormViewModel';
import { SeniorSnapshotListItemModel } from 'src/types/SeniorSnapshotListItemModel';
import {
  isValidCode,
  thunkExecuteAndReturnResultOrShowError,
} from '../../services/base.service';
import {
  approveSeniorSnapshots,
  deleteSeniorSnapshotFile,
  downloadSeniorSnapshotFile,
  getAllSeniorSnapshotFiles,
  getDataCollectionChecks,
  getSeniorSnapshotById,
  getSeniorSnapshotFilesByStudent,
  getSeniorSnapshotFormViewModel,
  getSeniorSnapshotLeas,
  getSeniorSnapshotListItems,
  getSeniorSnapshotSchoolsByLea,
  getSeniorSnapshotSchoolYears,
  uploadSeniorSnapshotFile,
} from '../../services/SeniorSnapshot.service';
import { SeniorSnapshotModel } from '../../types/SeniorSnapshotModel';

export const fetchSeniorSnapshotListItems = createAsyncThunk(
  'input/fetchSeniorSnapshotListItems',
  async (
    params: {
      schoolYear: string;
      lea: string;
      school: string;
      reviewStatus: string;
    },
    thunkAPI
  ) => {
    const { schoolYear, lea, school, reviewStatus } = params;
    return await thunkExecuteAndReturnResultOrShowError<
      SeniorSnapshotListItemModel[]
    >(
      thunkAPI,
      getSeniorSnapshotListItems(schoolYear, lea, school, reviewStatus, {
        signal: thunkAPI.signal,
      })
    );
  }
);

export const fetchSeniorSnapshotById = createAsyncThunk(
  'input/fetchSeniorSnapshotById',
  async (seniorSnapshotId: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SeniorSnapshotModel>(
      thunkAPI,
      getSeniorSnapshotById(seniorSnapshotId, { signal: thunkAPI.signal })
    );
  }
);

export const fetchSeniorSnapshotFormViewModel = createAsyncThunk(
  'input/fetchSeniorSnapshotFormViewModel',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SeniorSnapshotFormViewModel>(
      thunkAPI,
      getSeniorSnapshotFormViewModel({ signal: thunkAPI.signal })
    );
  }
);

export const fetchSeniorSnapshotSchoolYears = createAsyncThunk(
  'input/fetchSeniorSnapshotSchoolYears',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getSeniorSnapshotSchoolYears({ signal: thunkAPI.signal })
    );
  }
);

export const fetchDataCollectionCheck = createAsyncThunk(
  'seniorSnapshot/fetchDataCollectionCheck',
  async (schoolYear: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<boolean>(
      thunkAPI,
      getDataCollectionChecks(schoolYear, { signal: thunkAPI.signal })
    );
  }
);

export const fetchSeniorSnapshotLeas = createAsyncThunk(
  'input/fetchSeniorSnapshotLeas',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getSeniorSnapshotLeas({ signal: thunkAPI.signal })
    );
  }
);

export const fetchSeniorSnapshotSchoolsByLeaId = createAsyncThunk(
  'input/fetchSchoolsByLeaId',
  async (leaId: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getSeniorSnapshotSchoolsByLea(leaId, { signal: thunkAPI.signal })
    );
  }
);

export const approveSeniorSnapshotsThunk = createAsyncThunk(
  'input/approveSeniorSnapshots',
  async (ids: string[], thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<unknown>(
      thunkAPI,
      approveSeniorSnapshots(ids, { signal: thunkAPI.signal })
    );
  }
);

export const uploadSeniorSnapshotFileThunk = createAsyncThunk(
  'seniorSnapshot/uploadFile',
  async (
    params: {
      schoolYear: string; studentIdentifier: string;
      leaIdentifier: string; file: File;
    },
    thunkAPI
  ): Promise<{ id: string }> => {
    const response = await uploadSeniorSnapshotFile(
      params.schoolYear,
      params.studentIdentifier,
      params.leaIdentifier,
      params.file,
      {
        signal: thunkAPI.signal as AbortSignal,
      }
    );

    if (!isValidCode(response.status)) {
      const errorMessage =
        response.error?.detail ||
        response.error?.title ||
        'Failed to upload file. Please try again.';
      throw new Error(errorMessage);
    }

    return response.data;
  }
);

export const deleteSeniorSnapshotFileThunk = createAsyncThunk(
  'seniorSnapshot/deleteFile',
  async (params: { leaId: string; id: string }, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      deleteSeniorSnapshotFile(params.leaId, params.id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchAllSeniorSnapshotFilesThunk = createAsyncThunk(
  'seniorSnapshot/fetchAllFiles',
  async (
    leaId: string,
    thunkAPI
  ): Promise<SeniorSnapshotFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      SeniorSnapshotFileListItemModel[]
    >(
      thunkAPI,
      getAllSeniorSnapshotFiles(leaId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const downloadSeniorSnapshotFileThunk = createAsyncThunk(
  'seniorSnapshot/downloadFile',
  async (
    params: { leaId: string; metaId: string; fileName: string },
    thunkAPI
  ): Promise<void> => {
    try {
      const blob = await downloadSeniorSnapshotFile(
        params.leaId,
        params.metaId,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = params.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }
);

export const fetchSeniorSnapshotFilesByStudentThunk = createAsyncThunk(
  'seniorSnapshot/fetchFilesByStudent',
  async (
    params: {
      schoolYear: string;
      leaId: string;
      msisId: string;
      showLoading?: boolean;
    },
    thunkAPI
  ): Promise<SeniorSnapshotFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      SeniorSnapshotFileListItemModel[]
    >(
      thunkAPI,
      getSeniorSnapshotFilesByStudent(
        params.schoolYear,
        params.leaId,
        params.msisId,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      )
    );
  }
);
